import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, PlusCircle, Sun, CalendarDays, LayoutTemplate, Layers, Sparkles, Quote, Users, Users2, Facebook, FolderOpen, Building2, Plug, Settings2, LogOut, Globe, ScrollText, MessageCircle, Send, Megaphone, ClipboardList, Workflow } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Toaster } from "../../components/ui/sonner";

export default function StudioLayout({ base = "/studio" }) {
  const { user, authChecked, logout } = useApp();
  const navigate = useNavigate();
  const loginPath = base === "" ? "/login" : "/superadmin";

  if (!authChecked) return <div className="flex min-h-screen items-center justify-center bg-[#0D1321] text-sm text-slate-400">Loading Studio…</div>;
  if (!user) return <Navigate to={loginPath} replace />;
  if (!["admin", "greeting"].includes(user.role)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D1321] px-4" data-testid="studio-access-denied">
        <div className="w-full max-w-sm rounded-xl bg-white p-8 text-center">
          <p className="font-display text-lg font-bold text-navy">Staff Access Only</p>
          <p className="mt-2 text-sm text-slate-500">The Greeting Studio is restricted to admin and greeting staff accounts.</p>
          <button onClick={async () => { await logout(); navigate(loginPath); }} className="btn-dark mt-5 !rounded-md !px-5 !py-2 text-xs" data-testid="access-denied-logout">Logout</button>
        </div>
      </div>
    );
  }

  const M = [
    ["Dashboard", "", LayoutDashboard, true],
    ["Create Greeting", "/create", PlusCircle],
    ["Today's Greeting", "/daily", Sun],
    ["Greeting Library", "/library", LayoutTemplate],
    ["Templates", "/templates", Layers],
    ["Calendar", "/calendar", CalendarDays],
    ["AI Generator", "/ai", Sparkles],
    ["Quotes Library", "/quotes", Quote],
    ["Contacts", "/contacts", Users],
    ["WhatsApp", "/whatsapp", MessageCircle],
    ["WhatsApp Sender", "/sender", Send],
    ["Campaigns / Broadcast", "/campaigns", Megaphone],
    ["AI Broadcast Pipeline", "/pipeline", Workflow],
    ["Facebook Publishing", "/facebook", Facebook],
    ["Media Library", "/media", FolderOpen],
    ["People", "/people", Users2],
    ["Organizations", "/orgs", Building2],
    ["Integrations", "/integrations", Plug],
    ["API Logs", "/logs", ScrollText],
    ["History & Audit", "/audit", ClipboardList],
    ["Automation & Settings", "/settings", Settings2],
  ];

  return (
    <div className="flex min-h-screen bg-[#F5F3EE]" data-testid="studio-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-[#0D1321] lg:flex">
        <div className="border-b border-white/10 px-5 py-5">
          <p className="font-display text-lg font-bold text-white">Greeting Studio</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-saffron-500">Sahaj Sarathi · SCT Legal</p>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" aria-label="Studio">
          {M.map(([label, path, Icon, end]) => (
            <NavLink key={label} to={`${base}${path}`} end={!!end} data-testid={`studio-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-saffron-500 text-navy" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
              <Icon size={17} strokeWidth={1.8} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          {base === "" ? (
            <a href="https://www.sahajsarathi.com" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10" data-testid="studio-view-site"><Globe size={16} /> Main Website</a>
          ) : (
            <>
              <Link to="/" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10" data-testid="studio-view-site"><Globe size={16} /> Main Website</Link>
              <Link to="/superadmin" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10" data-testid="studio-superadmin"><LayoutDashboard size={16} /> Superadmin</Link>
            </>
          )}
          <button onClick={async () => { await logout(); navigate(loginPath); }} data-testid="studio-logout"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-red-300">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3 lg:hidden">
          <span className="font-display text-sm font-bold text-navy">Greeting Studio</span>
          <button onClick={async () => { await logout(); navigate(loginPath); }} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-red-500" data-testid="studio-logout-mobile">Logout</button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-gray-200 bg-white px-4 py-2 lg:hidden" aria-label="Studio mobile">
          {M.map(([label, path, Icon, end]) => (
            <NavLink key={label} to={`${base}${path}`} end={!!end}
              className={({ isActive }) => `whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold ${isActive ? "bg-navy text-white" : "text-slate-500"}`}>
              {label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 p-5 sm:p-8">
          <Outlet context={{ base }} />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
